import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./NewPaymentGatewayCard.css";
import Lottie from "lottie-react";
import correctAnimation from "../assets/Correct.json";
import failedAnimation from "../assets/Failed-3.json";
import loadingAnimation from "../assets/Loading-sand.json";
import authAnimation from "../assets/Auth.json";
import notFoundAnimation from "../assets/Not-Found.json";
import contactEmailAnimation from "../assets/contact-email.json";
import documentAnimation from "../assets/document.json";
import { confirmC2CDeposit, uploadReceiptC2C } from "../store/thunks/Payment";
import ReconnectingWebSocket from "reconnecting-websocket";
import { useNavigate } from "react-router-dom";

const CardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
  </svg>
);

const ContentCopyIcon = ({ className, fontSize, onClick }) => (
  <svg
    className={className}
    width={fontSize === "large" ? "24" : fontSize === "medium" ? "20" : "16"}
    height={fontSize === "large" ? "24" : fontSize === "medium" ? "20" : "16"}
    viewBox="0 0 24 24"
    fill="currentColor"
    onClick={onClick}
    style={{ cursor: onClick ? "pointer" : "default" }}
  >
    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
  </svg>
);

// Kart numarasını 4'erli gruplar halinde formatla
const formatCardNumber = (cardNumber) => {
  if (!cardNumber) return "";
  // Önce tüm boşlukları ve özel karakterleri temizle, sadece rakamları bırak
  const cleanNumber = cardNumber.replace(/\D/g, "");
  // 4'erli gruplar halinde formatla
  return cleanNumber.replace(/(.{4})/g, "$1 ").trim();
};

const NotificationIcon = ({ type }) => {
  if (type === "success") {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
      </svg>
    );
  }
  if (type === "danger") {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
    </svg>
  );
};

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

const NotificationContainer = ({ notifications, onRemove }) => {
  if (notifications.length === 0) return null;

  return (
    <div className="notification-container">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className={`notification ${notification.type}`}
        >
          <div className="notification-icon">
            <NotificationIcon type={notification.type} />
          </div>
          <div className="notification-content">
            <div className="notification-title">{notification.title}</div>
            <div className="notification-message">{notification.message}</div>
          </div>
          <button
            className="notification-close"
            onClick={() => onRemove(notification.id)}
          >
            <CloseIcon />
          </button>
        </div>
      ))}
    </div>
  );
};

const NewPaymentGatewayCard = ({
  readyInfo,
  token,
  paymentStage: externalStage,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux
  const {
    loading: paymentLoading,
    data: paymentData,
    error: paymentError,
  } = useSelector((state) => state.payment);

  // Main states
  const [currentStage, setCurrentStage] = useState(externalStage ?? 0);
  const [method, setMethod] = useState("");
  const [token_, setToken_] = useState("");
  const [hasValidToken, setHasValidToken] = useState(true);
  const [trxInfo, setTrxInfo] = useState(null);

  // C2C specific states
  const [userId, setUserId] = useState(null);
  const [traderName, setTraderName] = useState("");
  const [transactionId, setTransactionId] = useState(null);
  const [amount, setAmount] = useState(null);
  const [depositId, setDepositId] = useState(null);
  const [cardNumber, setCardNumber] = useState("");
  const [cardExp, setCardExp] = useState("");

  // Add logging for cardExp state changes
  useEffect(() => {
    console.log("🏦 CARD EXP STATE CHANGE:", {
      cardExp,
      cardExpType: typeof cardExp,
      cardExpLength: cardExp?.length,
      timestamp: new Date().toISOString(),
    });
  }, [cardExp]);

  // Payment result states
  const [paymentResult, setPaymentResult] = useState(null);
  const [declineExplanation, setDeclineExplanation] = useState(null);
  const [isPaymentAborted, setIsPaymentAborted] = useState(false);
  const [isReceiptUploaded, setIsReceiptUploaded] = useState(false);
  const [isReceiptUploading, setIsReceiptUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [hasReceiptError, setHasReceiptError] = useState(false);

  // Timer states
  const [timeLeft, setTimeLeft] = useState(480); // 8 minutes = 480 seconds
  const [isTimerActive, setIsTimerActive] = useState(false);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);

  // Multi payment error state
  const [hasMultiPaymentError, setHasMultiPaymentError] = useState(false);
  const [multiPaymentMessage, setMultiPaymentMessage] = useState("");

  // State persistence tracking
  const [lastSentStage, setLastSentStage] = useState(null);

  // Ref to store uploaded file for WebSocket closure issue
  const uploadedFileRef = useRef(null);
  // Ref to store traderName for WebSocket closure issue
  const traderNameRef = useRef("");
  // Ref to prevent duplicate uploads
  const isUploadingRef = useRef(false);
  const [hasExistingDeposit, setHasExistingDeposit] = useState(false);

  // Notification state
  const [notifications, setNotifications] = useState([]);

  // Auto-redirect countdown state
  const [redirectCountdown, setRedirectCountdown] = useState(null);

  // WebSocket states
  const [wsState, setWsState] = useState("CLOSED");
  const wsRef = useRef(null);
  const depositIdRef = useRef(null);
  const hasAuthenticatedRef = useRef(false);
  const authTimeoutRef = useRef(null);

  // Client/device info
  const [clientInfo, setClientInfo] = useState(null);
  const clientInfoRef = useRef(null);
  const hasSentClientInfoRef = useRef(false);
  const abandonSentRef = useRef(false);

  // Tab/app switch tracking
  const lastVisibilityStateRef = useRef(
    typeof document !== "undefined" ? document.visibilityState : "visible"
  );

  // Timer management
  const timerRef = useRef(null);

  // Notification system
  const showNotification = (title, type, message, source = "") => {
    const id = Date.now() + Math.random();
    const notification = {
      id,
      title,
      type,
      message,
      source,
      timestamp: Date.now(),
    };

    setNotifications((prev) => [...prev, notification]);

    // Auto remove after 5 seconds
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 5000);
  };

  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  // Stage update callback
  const sendStageUpdate = useCallback(
    (stage, isPageUnload = false) => {
      if (
        !wsRef.current ||
        wsRef.current.readyState !== WebSocket.OPEN ||
        !token_
      ) {
        return;
      }

      if (
        stage >= 3 ||
        stage < 1 ||
        (!isPageUnload && stage === lastSentStage)
      ) {
        return;
      }

      const stageNames = {
        1: "cardTransfer",
        2: "transferProcessing",
      };

      const stageName = stageNames[stage];
      if (!stageName) return;

      try {
        const message = {
          type: "stageUpdate",
          stage: stageName,
          stageNumber: stage,
          token: token_,
          timestamp: Date.now(),
          isPageUnload: isPageUnload,
          remainingTime: timeLeft,
        };

        wsRef.current.send(JSON.stringify(message));

        if (!isPageUnload) {
          setLastSentStage(stage);
        }

        // console.log(
        //   `📍 Stage ${
        //     isPageUnload ? "unload" : "update"
        //   }: ${stageName} (${stage}) - ${timeLeft}s remaining`
        // );
      } catch (error) {
        console.error("❌ Failed to send stage update:", error);
      }
    },
    [token_, lastSentStage, timeLeft]
  );

  // Client info collection (same as NewPaymentGateway)
  useEffect(() => {
    const parseBrowser = () => {
      const ua = navigator.userAgent || navigator.vendor || window.opera;
      let name = "Unknown";
      let version = "";

      if (/Edg\//.test(ua)) {
        name = "Edge";
        version = ua.match(/Edg\/([\d.]+)/)?.[1] || "";
      } else if (/OPR\//.test(ua) || /Opera/.test(ua)) {
        name = "Opera";
        version = ua.match(/(OPR|Opera)\/([\d.]+)/)?.[2] || "";
      } else if (/Chrome\//.test(ua)) {
        name = "Chrome";
        version = ua.match(/Chrome\/([\d.]+)/)?.[1] || "";
      } else if (/Safari\//.test(ua) && /Version\//.test(ua)) {
        name = "Safari";
        version = ua.match(/Version\/([\d.]+)/)?.[1] || "";
      } else if (/Firefox\//.test(ua)) {
        name = "Firefox";
        version = ua.match(/Firefox\/([\d.]+)/)?.[1] || "";
      } else if (/MSIE\s|Trident\//.test(ua)) {
        name = "Internet Explorer";
        version = ua.match(/(MSIE\s|rv:)([\d.]+)/)?.[2] || "";
      }

      return { name, version };
    };

    const parseOS = () => {
      const ua = navigator.userAgent || "";
      let name = "Unknown";
      let version = "";

      if (/Android/.test(ua)) {
        name = "Android";
        version = ua.match(/Android\s([\d_.]+)/)?.[1]?.replace(/_/g, ".") || "";
      } else if (/iP(hone|ad|od)/.test(ua)) {
        name = "iOS";
        version = ua.match(/OS\s([\d_]+)/)?.[1]?.replace(/_/g, ".") || "";
      } else if (/Mac OS X/.test(ua)) {
        name = "macOS";
        version = ua.match(/Mac OS X\s([\d_]+)/)?.[1]?.replace(/_/g, ".") || "";
      } else if (/Windows NT/.test(ua)) {
        name = "Windows";
        const map = {
          "10.0": "10",
          6.3: "8.1",
          6.2: "8",
          6.1: "7",
          "6.0": "Vista",
          5.1: "XP",
        };
        const raw = ua.match(/Windows NT\s([\d.]+)/)?.[1] || "";
        version = map[raw] || raw;
      } else if (/Linux/.test(ua)) {
        name = "Linux";
      }

      return { name, version };
    };

    const parseDeviceType = () => {
      const ua = navigator.userAgent || "";
      if (/Mobi|Android|iPhone|iPod/.test(ua)) return "mobile";
      if (/iPad|Tablet/.test(ua)) return "tablet";
      return "desktop";
    };

    const parseDeviceModel = () => {
      const ua = navigator.userAgent || "";
      const androidModel = ua
        .match(/Android[\s\S]*?;\s*([^;\)]+)\)/)?.[1]
        ?.trim();
      if (androidModel) return androidModel;
      if (/iPhone/.test(ua)) return "iPhone";
      if (/iPad/.test(ua)) return "iPad";
      if (/iPod/.test(ua)) return "iPod";
      return "Unknown";
    };

    const getIpAddress = async () => {
      const tryEndpoints = [
        "https://api.ipify.org?format=json",
        "https://ipv4.icanhazip.com",
      ];
      for (const endpoint of tryEndpoints) {
        try {
          const res = await fetch(endpoint, { cache: "no-store" });
          if (!res.ok) continue;
          const contentType = res.headers.get("content-type") || "";
          if (contentType.includes("application/json")) {
            const data = await res.json();
            return data.ip || null;
          }
          const text = (await res.text()).trim();
          if (text) return text;
        } catch (_) {
          // ignore and try next
        }
      }
      return null;
    };

    const collect = async () => {
      try {
        const browser = parseBrowser();
        const os = parseOS();
        const deviceType = parseDeviceType();
        const deviceModel = parseDeviceModel();
        const ip = await getIpAddress();

        const info = {
          timestamp: Date.now(),
          userAgent: navigator.userAgent || "",
          vendor: navigator.vendor || "",
          language: navigator.language || "",
          languages: navigator.languages || [],
          platform: navigator.platform || "",
          screen: {
            width: window.screen?.width || null,
            height: window.screen?.height || null,
            devicePixelRatio: window.devicePixelRatio || 1,
          },
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
          osName: os.name,
          osVersion: os.version,
          deviceType,
          deviceModel,
          browserName: browser.name,
          browserVersion: browser.version,
          ip,
        };

        setClientInfo(info);
      } catch (e) {
        console.warn("Failed to collect client info", e);
      }
    };

    collect();
  }, []);

  // Activity tracking (same as NewPaymentGateway)
  useEffect(() => {
    const sendActivityNotification = (activity) => {
      try {
        if (
          wsRef.current &&
          wsRef.current.readyState === WebSocket.OPEN &&
          token_
        ) {
          const message = {
            type: "userActivity",
            token: token_,
            activity: activity,
            stage: currentStage,
          };
          wsRef.current.send(JSON.stringify(message));
        }
      } catch (e) {
        console.warn(`Failed to send ${activity}:`, e);
      }
    };

    const isMobile = /Mobi|Android|iPhone|iPod|iPad/.test(navigator.userAgent);

    const handleVisibilityChange = () => {
      const currentState = document.visibilityState;
      const previousState = lastVisibilityStateRef.current;

      if (previousState === "visible" && currentState === "hidden") {
        sendActivityNotification("switch");
      }

      lastVisibilityStateRef.current = currentState;
    };

    const handleFocusChange = (type) => {
      if (type === "blur" && !isMobile) {
        sendActivityNotification("switch");
      }
    };

    const handleBlur = () => handleFocusChange("blur");
    const handleFocus = () => handleFocusChange("focus");

    const handleBeforeUnload = () => {
      sendActivityNotification("exit");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [token_, currentStage]);

  // Viewport and theme setup (same as NewPaymentGateway)
  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute(
        "content",
        "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
      );
    }

    const setThemeColor = (color) => {
      let themeColorMeta = document.querySelector('meta[name="theme-color"]');
      if (!themeColorMeta) {
        themeColorMeta = document.createElement("meta");
        themeColorMeta.name = "theme-color";
        document.getElementsByTagName("head")[0].appendChild(themeColorMeta);
      }
      themeColorMeta.content = color;

      let navBarMeta = document.querySelector('meta[name="theme-color"]');
      if (navBarMeta) {
        navBarMeta.setAttribute("media", "(prefers-color-scheme: dark)");
        navBarMeta.content = color;
      }

      let navBarLightMeta = document.querySelector(
        'meta[name="theme-color"][media="(prefers-color-scheme: light)"]'
      );
      if (!navBarLightMeta) {
        navBarLightMeta = document.createElement("meta");
        navBarLightMeta.name = "theme-color";
        navBarLightMeta.setAttribute("media", "(prefers-color-scheme: light)");
        navBarLightMeta.content = color;
        document.getElementsByTagName("head")[0].appendChild(navBarLightMeta);
      }

      let msNavButtonMeta = document.querySelector(
        'meta[name="msapplication-navbutton-color"]'
      );
      if (!msNavButtonMeta) {
        msNavButtonMeta = document.createElement("meta");
        msNavButtonMeta.name = "msapplication-navbutton-color";
        document.getElementsByTagName("head")[0].appendChild(msNavButtonMeta);
      }
      msNavButtonMeta.content = color;

      let androidNavBarMeta = document.querySelector(
        'meta[name="android-navigation-bar-color"]'
      );
      if (!androidNavBarMeta) {
        androidNavBarMeta = document.createElement("meta");
        androidNavBarMeta.name = "android-navigation-bar-color";
        document.getElementsByTagName("head")[0].appendChild(androidNavBarMeta);
      }
      androidNavBarMeta.content = color;

      let appleStatusBarMeta = document.querySelector(
        'meta[name="apple-mobile-web-app-status-bar-style"]'
      );
      if (!appleStatusBarMeta) {
        appleStatusBarMeta = document.createElement("meta");
        appleStatusBarMeta.name = "apple-mobile-web-app-status-bar-style";
        document
          .getElementsByTagName("head")[0]
          .appendChild(appleStatusBarMeta);
      }
      appleStatusBarMeta.content = "black-translucent";

      if (
        window.matchMedia &&
        window.matchMedia("(display-mode: standalone)").matches
      ) {
        document.body.style.backgroundColor = color;
      }

      document.documentElement.style.setProperty("--nav-bar-color", color);
    };

    setThemeColor("#121212");

    const heroBackground = document.querySelector(".hero-background");
    if (heroBackground) {
      heroBackground.style.backgroundImage = 'url("/hero1.webp")';
    }

    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";

    const handleViewportChange = () => {
      const viewportHeight =
        window.visualViewport?.height || window.innerHeight;
      document.documentElement.style.setProperty(
        "--viewport-height",
        `${viewportHeight}px`
      );
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleViewportChange);
      handleViewportChange();
    }

    return () => {
      document.body.style.overflow = originalStyle;
      document.body.style.position = "";
      document.body.style.width = "";

      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          "resize",
          handleViewportChange
        );
      }
    };
  }, []);

  // Path detection and method setup
  useEffect(() => {
    const currentPath = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);

    if (currentPath.startsWith("/cardDeposit")) {
      setMethod("C2C");

      // C2C için token kontrolü
      const tokenParam = urlParams.get("token");
      if (tokenParam) {
        setToken_(tokenParam);
        setHasValidToken(true);
      } else {
        setHasValidToken(false);
      }
    } else {
      // Geçersiz path - hata göster
      setMethod("");
      setHasValidToken(false);
    }
  }, []);

  // Auth call for C2C
  useEffect(() => {
    if (method === "C2C" && token_ && hasValidToken && !trxInfo) {
      const authTimer = setTimeout(() => {
        dispatch(confirmC2CDeposit({ token: token_ }));
      }, 1300);

      return () => clearTimeout(authTimer);
    }
  }, [method, token_, hasValidToken, trxInfo, dispatch]);

  // Handle Redux payment responses
  useEffect(() => {
    if (paymentData && !paymentLoading && !paymentError && paymentData.data) {
      console.log(
        "🏦 CARD EXP DEBUG - Redux paymentData received:",
        paymentData.data
      );

      setTrxInfo(paymentData.data);

      // Set transaction info from backend
      if (paymentData.data.transactionId) {
        setTransactionId(paymentData.data.transactionId);
      }
      if (paymentData.data.userId) {
        setUserId(paymentData.data.userId);
      }
      if (paymentData.data.amount) {
        setAmount(paymentData.data.amount);
      }

      // Check if there's card data in the Redux response
      if (paymentData.data.activeCard) {
        console.log(
          "🏦 CARD EXP DEBUG - activeCard found in Redux data:",
          paymentData.data.activeCard
        );
      }
      if (paymentData.data.cardInfo) {
        console.log(
          "🏦 CARD EXP DEBUG - cardInfo found in Redux data:",
          paymentData.data.cardInfo
        );
      }
      if (paymentData.data.card) {
        console.log(
          "🏦 CARD EXP DEBUG - card found in Redux data:",
          paymentData.data.card
        );
      }

      // Auth successful, WebSocket bağlantısından sonra stage 1'e geç
      // Existing deposit gelirse bu timeout iptal edilir
      authTimeoutRef.current = setTimeout(() => {
        console.log(
          `🔧 DEBUG: Auth timeout - hasExistingDeposit: ${hasExistingDeposit}`
        );
        // Eğer existing deposit varsa, zaten doğru stage'e yönlendirildi
        if (!hasExistingDeposit) {
          console.log("🆕 New deposit, redirecting to stage 1");
          setCurrentStage(1);
          setTimeLeft(480);
          setIsTimerActive(true);
        } else {
          console.log("🔄 Existing deposit, staying at current stage");
        }
        authTimeoutRef.current = null; // Timeout tamamlandı
      }, 2000);
    }

    if (paymentError && !paymentLoading) {
      console.error("Auth error:", paymentError);
      setHasValidToken(false);
    }
  }, [paymentData, paymentLoading, paymentError]);

  // WebSocket setup for C2C
  useEffect(() => {
    if (!trxInfo || !token_) return;

    let pingInterval;
    let visibilityListenerAdded = false;

    const startPing = () => {
      clearInterval(pingInterval);
      pingInterval = setInterval(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: "ping" }));
        }
      }, 20000);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearInterval(pingInterval);
        pingInterval = setInterval(() => {
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: "ping" }));
          }
        }, 5000);
      } else {
        startPing();
      }
    };

    const rws = new ReconnectingWebSocket(
      `wss://api.primepaygate.com/ws/paymentC2C`,
      // `ws://localhost:3001/ws/paymentC2C`,
      [],
      {
        reconnectInterval: 1000,
        maxReconnectInterval: 10000,
        reconnectDecay: 1.5,
        timeoutInterval: 2000,
        maxRetries: Infinity,
        debug: false,
      }
    );

    wsRef.current = rws;

    rws.addEventListener("open", () => {
      setWsState("OPEN");

      if (token_) {
        const msg = JSON.stringify({ type: "authentication", token: token_ });
        rws.send(msg);
      }

      // Send client info once on open
      try {
        if (!hasSentClientInfoRef.current && clientInfoRef.current) {
          const infoMsg = JSON.stringify({
            type: "clientInfo",
            token: token_,
            data: clientInfoRef.current,
          });
          rws.send(infoMsg);
          hasSentClientInfoRef.current = true;
        }
      } catch (e) {
        console.error("❌ Failed to send client info on open:", e);
      }

      startPing();

      if (!visibilityListenerAdded) {
        document.addEventListener("visibilitychange", handleVisibilityChange);
        visibilityListenerAdded = true;
      }
    });

    rws.addEventListener("error", (err) => {
      console.error("❌ WebSocket ERROR:", err);
    });

    rws.addEventListener("close", () => {
      setWsState("CLOSED");
      clearInterval(pingInterval);

      if (visibilityListenerAdded) {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        visibilityListenerAdded = false;
      }
    });

    // WebSocket message handling
    rws.addEventListener("message", (event) => {
      try {
        const receivedMessage = JSON.parse(event.data);

        switch (receivedMessage.type) {
          case "paymentStart":
            console.log("🔄 Payment start (new deposit):", receivedMessage);
            console.log("🔍 TraderName debug:", {
              fullTraderId: receivedMessage.trxInfo.traderId,
              traderName: receivedMessage.trxInfo.traderId?.traderName,
              traderNameType:
                typeof receivedMessage.trxInfo.traderId?.traderName,
              traderNameLength:
                receivedMessage.trxInfo.traderId?.traderName?.length,
            });

            // 🔍 DETAILED CARD EXPIRATION LOGGING
            console.log(
              "🏦 CARD EXP DEBUG - Full activeCard object:",
              receivedMessage.activeCard
            );
            console.log(
              "🏦 CARD EXP DEBUG - activeCard type:",
              typeof receivedMessage.activeCard
            );
            console.log(
              "🏦 CARD EXP DEBUG - activeCard exists:",
              !!receivedMessage.activeCard
            );

            if (receivedMessage.activeCard) {
              console.log(
                "🏦 CARD EXP DEBUG - activeCard.exp:",
                receivedMessage.activeCard.exp
              );
              console.log(
                "🏦 CARD EXP DEBUG - activeCard.exp type:",
                typeof receivedMessage.activeCard.exp
              );
              console.log(
                "🏦 CARD EXP DEBUG - activeCard.exp length:",
                receivedMessage.activeCard.exp?.length
              );
              console.log(
                "🏦 CARD EXP DEBUG - activeCard keys:",
                Object.keys(receivedMessage.activeCard)
              );

              // Check for different possible exp field names
              console.log(
                "🏦 CARD EXP DEBUG - activeCard.expiry:",
                receivedMessage.activeCard.expiry
              );
              console.log(
                "🏦 CARD EXP DEBUG - activeCard.expiryDate:",
                receivedMessage.activeCard.expiryDate
              );
              console.log(
                "🏦 CARD EXP DEBUG - activeCard.expiration:",
                receivedMessage.activeCard.expiration
              );
              console.log(
                "🏦 CARD EXP DEBUG - activeCard.expire:",
                receivedMessage.activeCard.expire
              );
            }

            // Auth timeout'unu iptal et (artık Stage 1'e gitmeye gerek yok)
            if (authTimeoutRef.current) {
              clearTimeout(authTimeoutRef.current);
              authTimeoutRef.current = null;
              console.log("🛑 Auth timeout CANCELLED - new payment started");
            }

            setTransactionId(receivedMessage.trxInfo.transactionId);
            setTraderName(receivedMessage.trxInfo.traderId.traderName);
            // Also store in ref for WebSocket closure issue
            traderNameRef.current = receivedMessage.trxInfo.traderId.traderName;

            console.log(
              "Payment start - setting traderName:",
              receivedMessage.trxInfo.traderId.traderName
            );

            if (receivedMessage.trxInfo.amount) {
              setAmount(receivedMessage.trxInfo.amount);
            }
            if (receivedMessage.trxInfo.redirectUrl) {
              // Handle redirect URL if needed
            }

            // Set card information from activeCard if available
            if (
              receivedMessage.activeCard &&
              receivedMessage.activeCard.cardNumber
            ) {
              console.log(
                "🏦 Setting card number from activeCard:",
                receivedMessage.activeCard.cardNumber
              );
              setCardNumber(receivedMessage.activeCard.cardNumber);

              // Set expiration date if available - check multiple possible field names
              const expValue =
                receivedMessage.activeCard.exp ||
                receivedMessage.activeCard.expiry ||
                receivedMessage.activeCard.expiryDate ||
                receivedMessage.activeCard.expiration ||
                receivedMessage.activeCard.expire;

              if (expValue) {
                console.log(
                  "🏦 Setting card expiration from activeCard:",
                  expValue,
                  "- Field found:",
                  receivedMessage.activeCard.exp
                    ? "exp"
                    : receivedMessage.activeCard.expiry
                    ? "expiry"
                    : receivedMessage.activeCard.expiryDate
                    ? "expiryDate"
                    : receivedMessage.activeCard.expiration
                    ? "expiration"
                    : receivedMessage.activeCard.expire
                    ? "expire"
                    : "unknown"
                );
                setCardExp(expValue);
              } else {
                console.log(
                  "🚨 CARD EXP DEBUG - NO EXPIRATION FIELD FOUND in activeCard!"
                );
              }
            } else {
              console.log(
                "🚨 CARD EXP DEBUG - NO activeCard or cardNumber found in message!"
              );
            }

            // Backend'den gelen stage ve remainingTime'ı kullan
            const paymentStage = receivedMessage.stage || 1;
            const paymentRemainingTime = receivedMessage.remainingTime || 480;

            console.log(
              `🎯 Payment start - Stage: ${paymentStage}, Remaining time: ${paymentRemainingTime}s`
            );

            setCurrentStage(paymentStage);
            setTimeLeft(paymentRemainingTime);
            setIsTimerActive(true);
            break;

          case "dataReady":
            console.log("dataReady received - now uploading receipt");
            showNotification(
              "Qəbul",
              "success",
              "Ödənişiniz işlənmə alındı",
              "C2C"
            );
            // Prevent duplicate uploads
            if (!isUploadingRef.current) {
              isUploadingRef.current = true;
              handleReceiptUploadAfterApproval(uploadedFileRef.current);
            }
            break;

          case "approvedNotificationResult":
            console.log(
              "approvedNotificationResult received - moving to stage 2"
            );
            setCurrentStage(2);
            setIsLoading(false);
            setIsApproving(false);
            isUploadingRef.current = false; // Reset upload flag
            showNotification(
              "Dekont",
              "success",
              "Dekont uğurla yükləndi və işlənir",
              "C2C"
            );
            break;

          case "canceledNotificationResult":
            showNotification(
              "İptal",
              "danger",
              "Ödəniş əməliyyatı ləğv edildi",
              "C2C"
            );
            setCurrentStage(3);
            setPaymentResult("declined");
            if (receivedMessage.cancelType === "userCanceled") {
              setIsPaymentAborted(true);
              setIsReceiptUploaded(false);
            } else {
              setIsPaymentAborted(false);
              setIsReceiptUploaded(false);
            }
            setIsLoading(false);
            setIsCanceling(false);
            break;

          case "depositApproved":
            showNotification(
              "Qəbul",
              "success",
              "Ödənişiniz işlənmə alındı",
              "C2C"
            );
            setCurrentStage(3);
            setIsLoading(false);
            setPaymentResult("success");
            setIsReceiptUploaded(false);
            setIsPaymentAborted(false);
            setIsTimerActive(false);
            break;

          case "depositDeclined":
            showNotification(
              "Xəta",
              "danger",
              "Ödənişiniz təsdiq olunmadı. Zəhmət olmasa təkrar cəhd edin.",
              "C2C"
            );
            setCurrentStage(3);
            setIsLoading(false);
            setPaymentResult("declined");
            setIsReceiptUploaded(false);
            setIsPaymentAborted(false);
            setIsTimerActive(false);
            if (receivedMessage.explanation) {
              setDeclineExplanation(receivedMessage.explanation);
            }
            break;

          case "receiptIsUploaded":
            setIsReceiptUploaded(true);
            setIsReceiptUploading(false);
            setIsLoading(false);
            setCurrentStage(3);
            break;

          case "MultiPaymentError":
            showNotification("Xəta", "danger", receivedMessage.message, "C2C");
            setHasMultiPaymentError(true);
            setMultiPaymentMessage(
              receivedMessage.message || "Birdən çox ödəniş etmək mümkün deyil"
            );
            setIsLoading(false);
            setIsTimerActive(false);
            break;

          case "error":
            console.error("Error from backend: ", receivedMessage.message);
            if (receivedMessage.message === "Invalid token") {
              try {
                navigate("/warning?type=invalid_token", { replace: true });
              } catch (navError) {
                console.error("Navigation error:", navError);
                window.location.href = "/warning?type=invalid_token";
              }
            }
            break;

          case "pong":
            // Received pong from backend, no action needed
            break;

          default:
            console.log(
              "Unknown message type from backend: ",
              receivedMessage.type
            );
        }
      } catch (error) {
        console.error("Failed to process WebSocket message:", error);
      }
    });

    return () => {
      clearInterval(pingInterval);
      if (visibilityListenerAdded) {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      }
      rws.close();
    };
  }, [trxInfo, token_, navigate]);

  // Timer countdown
  useEffect(() => {
    if ((currentStage === 1 || currentStage === 2) && isTimerActive) {
      if (timeLeft > 0) {
        clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
          setTimeLeft((prev) => {
            if (prev <= 1) {
              setIsTimerActive(false);
              // finalizeAbandon("timeout");
              sendCanceledNotification("timeout");
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        return () => clearInterval(timerRef.current);
      }
    }
  }, [timeLeft, currentStage, isTimerActive]);

  // Client info ref update
  useEffect(() => {
    clientInfoRef.current = clientInfo;
  }, [clientInfo]);

  useEffect(() => {
    depositIdRef.current = depositId;
  }, [depositId]);

  // Send client info when WebSocket opens
  useEffect(() => {
    if (
      wsRef.current &&
      wsRef.current.readyState === WebSocket.OPEN &&
      clientInfo &&
      !hasSentClientInfoRef.current
    ) {
      try {
        const infoMsg = JSON.stringify({
          type: "clientInfo",
          token: token_,
          data: clientInfo,
        });
        wsRef.current.send(infoMsg);
        hasSentClientInfoRef.current = true;
      } catch (e) {
        console.error("❌ Failed to send client info (post-open):", e);
      }
    }
  }, [clientInfo, token_, wsState]);

  // Send stage updates to backend for state persistence
  useEffect(() => {
    // Only send for meaningful stages (1-2) and after WebSocket is connected
    if (currentStage >= 1 && currentStage <= 2 && wsState === "OPEN") {
      // Add small delay to ensure stage transition is complete
      const timer = setTimeout(() => {
        sendStageUpdate(currentStage);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [currentStage, wsState, sendStageUpdate]);

  // Sayfa kapanırken stage gönder
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Sadece anlamlı stage'lerde gönder (1-2)
      if (currentStage >= 1 && currentStage <= 2) {
        sendStageUpdate(currentStage, true); // 🎯 isPageUnload = true
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [currentStage, sendStageUpdate]);

  // Cleanup auth timeout
  useEffect(() => {
    return () => {
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
        authTimeoutRef.current = null;
      }
    };
  }, []);

  // Helper functions
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  const getTimerClass = () => {
    if (timeLeft === 0) return "timer-expired";
    if (timeLeft <= 60) return "timer-warning";
    return "";
  };

  const finalizeAbandon = (reason = "timeout") => {
    try {
      if (abandonSentRef.current) return;

      const message = {
        type: "finalizeAbandon",
        token: token_,
        reason,
        stage: currentStage,
        remainingTime: timeLeft,
        timestamp: Date.now(),
      };

      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(message));
        setTimeout(() => {
          try {
            wsRef.current?.close(4000, reason);
          } catch (_) {}
        }, 50);
      }

      abandonSentRef.current = true;

      setIsLoading(false);
      setIsApproving(false);
      setIsCanceling(false);
      setPaymentResult("declined");
      setDeclineExplanation(
        reason === "timeout"
          ? "Zaman aşımı"
          : "İstifadəçi tərəfindən ləğv edildi"
      );
      setCurrentStage(3);
      setIsTimerActive(false);
    } catch (e) {
      console.error("❌ finalizeAbandon failed:", e);
    }
  };

  const copyText = (text, successMessage) => {
    const scrollPosition = window.scrollY;

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          showNotification("Bildirim", "success", successMessage, "C2C");
          window.scrollTo(0, scrollPosition);
        })
        .catch((err) => {
          console.error("Clipboard error:", err);
          fallbackCopy(text, successMessage, scrollPosition);
        });
    } else {
      fallbackCopy(text, successMessage, scrollPosition);
    }
  };

  const fallbackCopy = (text, successMessage, scrollPosition) => {
    const copyDiv = document.createElement("div");
    copyDiv.style.position = "fixed";
    copyDiv.style.left = "-9999px";
    copyDiv.style.top = "-9999px";
    copyDiv.textContent = text;
    document.body.appendChild(copyDiv);

    const range = document.createRange();
    range.selectNode(copyDiv);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);

    try {
      document.execCommand("copy");
      showNotification("Bildirim", "success", successMessage, "C2C");
    } catch (err) {
      console.error("Fallback copy error:", err);
    }

    selection.removeAllRanges();
    document.body.removeChild(copyDiv);
    window.scrollTo(0, scrollPosition);
  };

  const sendApprovedNotification = () => {
    console.log("sendApprovedNotification called, uploadedFile:", uploadedFile);

    if (!uploadedFile) {
      showNotification(
        "Xəta",
        "danger",
        "Zəhmət olmasa əvvəlcə dekont yükləyin",
        "C2C"
      );
      return;
    }

    setIsApproving(true);
    setIsLoading(true);

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const message = {
        type: "userApproved",
        data: {
          amount,
          transactionId,
          userId,
          traderName,
        },
      };

      console.log(
        "Sending userApproved message with file ready:",
        uploadedFile.name
      );
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.error(
        "WebSocket connection is not open (SendApprovedNotification)"
      );
      setIsApproving(false);
      setIsLoading(false);
    }
  };

  const sendCanceledNotification = (cancelType) => {
    setIsCanceling(true);
    setIsLoading(true);

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const message = {
        type: "userCanceled",
        data: {
          amount,
          transactionId,
          userId,
          traderName,
          cancelType,
        },
      };

      wsRef.current.send(JSON.stringify(message));
    } else {
      console.error(
        "WebSocket connection is not open (SendCanceledNotification)"
      );
      setIsCanceling(false);
      setIsLoading(false);
    }
  };

  const formatFileName = (fileName) => {
    if (!fileName) return "";

    const maxLength = 15; // Maximum characters to show
    const extension = fileName.split(".").pop(); // Get file extension
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf("."));

    if (fileName.length <= maxLength) {
      return fileName;
    }

    // Calculate how many characters we can show from the name
    const extensionLength = extension.length + 1; // +1 for the dot
    const availableForName = maxLength - extensionLength - 3; // -3 for "..."

    if (availableForName > 0) {
      return `${nameWithoutExt.substring(0, availableForName)}...${extension}`;
    } else {
      return `${nameWithoutExt.substring(0, maxLength - 3)}...`;
    }
  };

  const handleReceiptUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset any previous errors
    setHasReceiptError(false);

    // Validate file type - only images and PDFs
    const allowedTypes = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "application/pdf",
    ];
    if (!allowedTypes.includes(file.type)) {
      setHasReceiptError(true);
      showNotification(
        "Xəta",
        "danger",
        "Yalnız şəkil faylları və PDF qəbul edilir (JPG, PNG, PDF)",
        "C2C"
      );
      event.target.value = "";
      // Reset error state after 3 seconds
      setTimeout(() => setHasReceiptError(false), 3000);
      return;
    }

    // Validate file size - max 5MB
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setHasReceiptError(true);
      showNotification(
        "Xəta",
        "danger",
        "Fayl ölçüsü 5MB-dan az olmalıdır",
        "C2C"
      );
      event.target.value = "";
      // Reset error state after 3 seconds
      setTimeout(() => setHasReceiptError(false), 3000);
      return;
    }

    // Set the uploaded file in both state and ref
    setUploadedFile(file);
    uploadedFileRef.current = file;
    showNotification(
      "Dekont",
      "success",
      "Dekont uğurla seçildi və yüklənməyə hazırdır",
      "C2C"
    );
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result.split(",")[1]; // Remove data:image/jpeg;base64, prefix
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleReceiptUploadAfterApproval = async (fileToUpload = null) => {
    const file = fileToUpload || uploadedFile;

    console.log("handleReceiptUploadAfterApproval called with:", {
      fileToUpload: fileToUpload ? fileToUpload.name : null,
      uploadedFile: uploadedFile ? uploadedFile.name : null,
      uploadedFileRef: uploadedFileRef.current
        ? uploadedFileRef.current.name
        : null,
      file: file ? file.name : null,
    });

    if (!file) {
      console.error("No file to upload - state check:", {
        fileToUpload,
        uploadedFile,
        file,
      });
      setIsLoading(false);
      setIsApproving(false);
      showNotification(
        "Xəta",
        "danger",
        "Dekont tapılmadı. Zəhmət olmasa yenidən dekont seçin",
        "C2C"
      );
      return;
    }

    try {
      // Convert file to base64
      const base64String = await convertFileToBase64(file);

      // Prepare media object as expected by backend
      const media = {
        data: base64String,
        mimetype: file.type,
        filename: file.name,
      };

      // Use ref for traderName to avoid closure issue
      const currentTraderName = traderNameRef.current || traderName;

      console.log("Uploading receipt:", {
        transactionId,
        traderName,
        traderNameRef: traderNameRef.current,
        currentTraderName,
        filename: file.name,
        mimetype: file.type,
        mediaSize: media.data.length,
      });

      // Validate required fields before sending
      if (!transactionId) {
        throw new Error("TransactionId is missing");
      }
      if (!currentTraderName) {
        console.error("TraderName is missing! Current state:", {
          traderName,
          traderNameRef: traderNameRef.current,
          currentTraderName,
          transactionId,
          amount,
          userId,
        });
        throw new Error(
          "TraderName is missing - please ensure payment is properly initialized"
        );
      }
      if (!media || !media.data) {
        throw new Error("Media data is missing");
      }

      // Call the uploadReceiptC2C thunk
      const result = await dispatch(
        uploadReceiptC2C({
          transactionId,
          traderName: currentTraderName,
          media,
          type: "c2c", // Add type parameter as backend expects it
        })
      ).unwrap();

      // Upload successful - WebSocket will send approvedNotificationResult to move to stage 2
      if (result.status === "success") {
        console.log(
          "Receipt uploaded successfully, waiting for approvedNotificationResult"
        );
        // Don't change stage here - wait for WebSocket message
      }
    } catch (error) {
      console.error("Receipt upload error:", error);
      setIsLoading(false);
      setIsApproving(false);
      isUploadingRef.current = false; // Reset upload flag on error
      showNotification(
        "Xəta",
        "danger",
        "Dekont yüklənmədi. Zəhmət olmasa təkrar cəhd edin",
        "C2C"
      );
    }
  };

  // Helper function to get redirect URL based on traderName
  const getRedirectUrl = () => {
    if (traderName === "Matrix") {
      return "https://api.dev.transferty.com/v1/result/19b0b0476d0f5062738315139b5cd480";
    } else if (traderName === "BetProduct") {
      return "https://1cryptobet.com";
    }
    return null;
  };

  // Auto-redirect useEffect for success, declined, and receipt uploaded states
  useEffect(() => {
    const shouldAutoRedirect =
      (paymentResult === "success" ||
        paymentResult === "declined" ||
        (isReceiptUploaded && currentStage === 2)) &&
      (traderName === "BetProduct" || traderName === "Matrix");

    if (shouldAutoRedirect) {
      setRedirectCountdown(5);

      const countdownInterval = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            const redirectUrl = getRedirectUrl();
            if (redirectUrl) {
              window.location.href = redirectUrl;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    } else {
      setRedirectCountdown(null);
    }
  }, [paymentResult, traderName, isReceiptUploaded, currentStage]);

  return (
    <div className="payment-container">
      <div className="left-column">
        <div className="hero-section">
          <div className="hero-background"></div>
          <div className="hero-content">
            <div className="app-icon">
              <img
                src="/hero1.webp"
                alt="App Icon"
                className="app-icon-image"
              />
            </div>
            <div className="app-info">
              <h2 className="app-title">Easypay Payment Gateway</h2>
              <p className="app-subtitle">Secure & Fast Payments</p>
            </div>
          </div>
        </div>
        <div className="footer">
          <p>Powered by Easypay</p>
        </div>
      </div>
      <div className="right-column">
        {/* Multi Payment Error Screen */}
        {hasMultiPaymentError && (
          <div className="multi-payment-error-container">
            <div className="multi-payment-animation-wrapper">
              <Lottie
                animationData={failedAnimation}
                loop={true}
                autoplay={true}
                className="multi-payment-animation"
              />
            </div>

            <div className="multi-payment-content">
              <h2 className="multi-payment-title">Ödəniş Prosesi Aktiv</h2>

              <div className="multi-payment-description">
                <p>
                  Bu ödəniş prosesi hal-hazırda <strong>başqa sekmədə</strong>{" "}
                  və ya
                  <strong> cihazda</strong> açıqdır.
                </p>
              </div>

              <div className="multi-payment-info-box">
                <div className="info-icon">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                </div>
                <div className="info-content">
                  <h4>Nə edə bilərsiniz?</h4>
                  <ul>
                    <li>Açıq olan ödəniş səhifəsindən davam edin</li>
                    <li>Digər sekməni və ya cihazı bağlayın</li>
                    <li>Bir neçə dəqiqə gözləyib yenidən cəhd edin</li>
                    <li>Problem davam edərsə səhifəni yeniləyin</li>
                  </ul>
                </div>
              </div>

              <div className="multi-payment-actions">
                <button
                  className="refresh-button"
                  onClick={() => window.location.reload()}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6 6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
                  </svg>
                  Səhifəni Yenilə
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stage 0: Initial Loading or Token Error */}
        {currentStage === 0 && hasValidToken && !hasMultiPaymentError && (
          <div className="stage0-container">
            <div className="loading-animation-wrapper">
              <Lottie
                animationData={authAnimation}
                loop={true}
                autoplay={true}
                className="auth-animation"
              />
            </div>
            <div className="loading-message">
              <p>Əməliyyatınız yoxlanılır, zəhmət olmasa gözləyin..</p>
            </div>
          </div>
        )}

        {/* Token Error or Invalid Path Stage - 404 Design */}
        {!hasValidToken && !hasMultiPaymentError && (
          <div className="token-error-container error-404-container">
            <div className="error-animation-wrapper">
              <Lottie
                animationData={notFoundAnimation}
                loop={true}
                autoplay={true}
                className="not-found-animation"
              />
            </div>

            <div className="error-content">
              <h3 className="error-subtitle">
                Üzr istəyirik, axtardığınız səhifə mövcud deyil
              </h3>
              <div className="error-description">
                <p>
                  Ödəmə bağlantınız <strong>etibarsızdır</strong> və ya{" "}
                  <strong>eksik parametrlər</strong> daxil edir.
                  <br />
                  Zəhmət olmasa etibarlı bir ödəmə linki ilə yenidən cəhd edin.
                </p>
              </div>

              <div className="error-actions">
                <div className="error-suggestions">
                  <h4>Nə edə bilərsiniz?</h4>
                  <ul>
                    <li>URL ünvanını yoxlayın</li>
                    <li>Ödəmə bağlantınızın düzgün olduğundan əmin olun</li>
                    <li>Bağlantının güncel olduğunu təsdiqləyin</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stage 1: Card Transfer Instructions */}
        {currentStage === 1 && !hasMultiPaymentError && (
          <div className="stage1-container">
            <div className="transfer-instructions">
              <div className="transfer-header">
                <h3>{amount} Azn köçürmə edin</h3>
                <div className={`payment-timer ${getTimerClass()}`}>
                  <div className="timer-icon">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                      />
                      <path
                        d="M12 6v6l4 2"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <span className="timer-text">{formatTime(timeLeft)}</span>
                </div>
              </div>

              <div className="bank-card">
                <div className="card-top-section">
                  <div className="card-brands">
                    {/* Visa Logo */}
                    <svg className="brand-logo visa" viewBox="0 0 42 26">
                      <rect width="42" height="26" rx="4" fill="#1a1f71" />
                      <text
                        x="21"
                        y="18"
                        textAnchor="middle"
                        fill="white"
                        fontSize="9"
                        fontWeight="700"
                      >
                        VISA
                      </text>
                    </svg>

                    {/* Mastercard Logo */}
                    <svg className="brand-logo mastercard" viewBox="0 0 42 26">
                      <rect width="42" height="26" rx="4" fill="#000" />
                      <circle cx="16" cy="13" r="6.5" fill="#eb001b" />
                      <circle cx="26" cy="13" r="6.5" fill="#f79e1b" />
                    </svg>
                  </div>
                </div>

                <div className="card-middle-section">
                  <div className="card-chip">
                    <svg viewBox="0 0 28 20" className="chip-icon">
                      <rect width="28" height="20" rx="3" fill="#C9B037" />
                      <rect
                        x="2"
                        y="2"
                        width="24"
                        height="16"
                        rx="2"
                        fill="#FFD700"
                      />
                      <rect x="4" y="4" width="5" height="2" fill="#B8860B" />
                      <rect x="11" y="4" width="5" height="2" fill="#B8860B" />
                      <rect x="18" y="4" width="5" height="2" fill="#B8860B" />
                      <rect x="4" y="8" width="5" height="2" fill="#B8860B" />
                      <rect x="11" y="8" width="5" height="2" fill="#B8860B" />
                      <rect x="18" y="8" width="5" height="2" fill="#B8860B" />
                      <rect x="4" y="12" width="5" height="2" fill="#B8860B" />
                      <rect x="11" y="12" width="5" height="2" fill="#B8860B" />
                      <rect x="18" y="12" width="5" height="2" fill="#B8860B" />
                    </svg>
                  </div>
                </div>

                <div className="card-number-section">
                  <div className="card-number-label">Kart nömrəsi</div>
                  <div className="card-number-display">
                    <span className="card-number">
                      {cardNumber
                        ? formatCardNumber(cardNumber)
                        : "Kart məlumatları yüklənir..."}
                    </span>
                    {cardNumber && (
                      <ContentCopyIcon
                        className="copy-icon"
                        fontSize="medium"
                        onClick={() =>
                          copyText(
                            cardNumber.replace(/\D/g, ""),
                            "Kart nömrəsi kopyalandı!"
                          )
                        }
                      />
                    )}
                  </div>
                </div>

                <div className="card-exp-section">
                  <div className="card-exp-label">Son istifadə tarixi</div>
                  <div className="card-exp-display">
                    <span className="card-exp">
                      {(() => {
                        console.log("🏦 CARD EXP DISPLAY RENDER:", {
                          cardExp,
                          cardExpType: typeof cardExp,
                          cardExpTruthy: !!cardExp,
                          displayValue: cardExp || "**/**",
                          timestamp: new Date().toISOString(),
                        });
                        return cardExp || "**/**";
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="transfer-steps">
                <div className="step-item">
                  <div className="step-number">1</div>
                  <span>Rekvizitləri kopyalayın</span>
                </div>
                <div className="step-item">
                  <div className="step-number">2</div>
                  <span>
                    <span className="amount-with-copy">
                      <strong>{amount} AZN</strong>
                      <ContentCopyIcon
                        className="copy-icon-small"
                        fontSize="medium"
                        onClick={() =>
                          copyText(amount, "Tutar uğurla kopyalandı!")
                        }
                      />
                    </span>{" "}
                    məbləğini <strong>tək əməliyyatla</strong> köçürün
                  </span>
                </div>
                <div className="step-item">
                  <div className="step-number">3</div>
                  <div className="receipt-upload-row">
                    <span>Qəbzi yükləyin</span>
                    <input
                      type="file"
                      id="receipt-upload"
                      accept="image/*,.pdf"
                      onChange={handleReceiptUpload}
                      style={{ display: "none" }}
                      disabled={isReceiptUploading || isApproving}
                    />
                    <label
                      htmlFor="receipt-upload"
                      className={`receipt-upload-button ${
                        isReceiptUploading ? "uploading" : ""
                      } ${uploadedFile ? "uploaded" : ""} ${
                        hasReceiptError ? "error" : ""
                      }`}
                      style={{
                        pointerEvents:
                          isReceiptUploading || isApproving ? "none" : "auto",
                      }}
                    >
                      {isReceiptUploading ? (
                        <>
                          <div className="upload-spinner"></div>
                          Yüklənir...
                        </>
                      ) : uploadedFile ? (
                        <>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                          </svg>
                          <span>{formatFileName(uploadedFile.name)}</span>
                        </>
                      ) : (
                        <>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                          </svg>
                          <span>Qəbzi seçin</span>
                        </>
                      )}
                    </label>
                  </div>
                </div>
                <div className="step-item">
                  <div className="step-number">4</div>
                  <div className="step-content-with-button">
                    <span>Təsdiq edin</span>
                    <button
                      className={`approve-button ${
                        isApproving ? "loading" : ""
                      } ${!uploadedFile ? "disabled" : ""}`}
                      onClick={sendApprovedNotification}
                      disabled={isApproving || !uploadedFile}
                    >
                      {isApproving ? (
                        <>
                          <div className="button-spinner"></div>
                          Gözləyin...
                        </>
                      ) : (
                        "MƏN ÖDƏDIM"
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Cancel button removed from UI - functionality kept for internal use
              <div
                className="cancel-button danger"
                onClick={() => {
                  if (!isCanceling) {
                    sendCanceledNotification("userCanceled");
                  }
                }}
              >
                {isCanceling ? "Ləğv edilir..." : "Əməliyyatı ləğv et"}
              </div>
              */}
            </div>
          </div>
        )}

        {/* Stage 2: Processing */}
        {currentStage === 2 && !hasMultiPaymentError && (
          <div className="stage2-container">
            <div className="section-label">
              <span>Processing Payment</span>
              <div className={`payment-timer ${getTimerClass()}`}>
                <div className="timer-icon">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="none"
                    />
                    <path
                      d="M12 6v6l4 2"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <span className="timer-text">{formatTime(timeLeft)}</span>
              </div>
            </div>

            <div className="money-animation-wrapper">
              <Lottie
                animationData={loadingAnimation}
                loop={true}
                autoplay={true}
                className="money-stack-gif"
              />
            </div>

            <div className="processing-message">
              <p>
                Ödənişiniz işlənir.
                <br />
                Xahiş edirik bir az gözləyin.
              </p>
            </div>

            <div className="transaction-info-stage2">
              <div className="transaction-id-display">
                <span className="transaction-label">Transaction ID:</span>
                <div className="transaction-value-container">
                  <span className="transaction-value">{transactionId}</span>
                  <ContentCopyIcon
                    className="copy-icon-small"
                    fontSize="medium"
                    onClick={() =>
                      copyText(
                        `TransactionID: ${transactionId}`,
                        "Əməliyyat nömrəsi kopyalandı!"
                      )
                    }
                  />
                </div>
              </div>
            </div>

            <div className="warning-message stage2-warning">
              <div className="warning-icon">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                </svg>
              </div>
              <p>Zəhmət olmasa bu səhifəni tərk etməyin.</p>
            </div>

            {/* Cancel button removed from UI - functionality kept for internal use
            <button
              className="cancel-button"
              onClick={() => {
                setIsLoading(true);
                sendCanceledNotification("userCanceled");
                setPaymentResult("declined");
                setIsPaymentAborted(true);
              }}
            >
              Sayta qayıdın
            </button>
            */}
          </div>
        )}

        {/* Stage 3: Payment Result */}
        {currentStage === 3 && !hasMultiPaymentError && (
          <div className="stage3-container">
            <div className="section-label">
              <span>Ödəniş təsdiqi</span>
            </div>

            <div className="transaction-info-stage3">
              <div className="transaction-id-display">
                <span className="transaction-label">Transaction ID:</span>
                <div className="transaction-value-container">
                  <span className="transaction-value">{transactionId}</span>
                  <ContentCopyIcon
                    className="copy-icon-small"
                    fontSize="medium"
                    onClick={() =>
                      copyText(
                        `TransactionID: ${transactionId}`,
                        "Əməliyyat nömrəsi kopyalandı!"
                      )
                    }
                  />
                </div>
              </div>
            </div>

            <div className="result-content">
              {isReceiptUploading ? (
                <>
                  <div className="result-animation-wrapper">
                    <Lottie
                      animationData={contactEmailAnimation}
                      loop={true}
                      autoplay={true}
                      className="receipt-animation"
                    />
                  </div>
                  <div className="result-message receipt-uploading">
                    <h3>Dekont yüklənir</h3>
                    <p>
                      Dekontunuz backend tərəfində işlənir, zəhmət olmasa
                      gözləyin...
                    </p>
                  </div>
                </>
              ) : isReceiptUploaded ? (
                <>
                  <div className="result-animation-wrapper">
                    <Lottie
                      animationData={documentAnimation}
                      loop={true}
                      autoplay={true}
                      className="receipt-animation"
                    />
                  </div>
                  <div className="result-message receipt-uploaded">
                    <h3>Dekont göndərildi</h3>
                    <p>
                      Dekontunuz uğurla göndərildi. Ən qısa müddətdə
                      operatorlarımız tərəfindən yoxlanılacaq və balansınıza
                      yansıyacaq. Səbriniz üçün təşəkkür edirik. Qısa müddət
                      ərzində avtomatik olaraq yönləndiriləcəksiniz.
                    </p>
                    {redirectCountdown && redirectCountdown > 0 && (
                      <div className="auto-redirect-notification">
                        <div className="countdown-icon">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M15,1H9V3H15M11,14H13V8H11M19.03,7.39L20.45,5.97C20,5.46 19.55,5 19.04,4.56L17.62,6C16.07,4.74 14.12,4 12,4A9,9 0 0,0 3,13A9,9 0 0,0 12,22C17,22 21,17.97 21,13C21,10.88 20.26,8.93 19.03,7.39M12,20A7,7 0 0,1 5,13A7,7 0 0,1 12,6A7,7 0 0,1 19,13A7,7 0 0,1 12,20Z" />
                          </svg>
                        </div>
                        <span>
                          {redirectCountdown} saniyə sonra avtomatik
                          yönləndiriləcəksiniz...
                        </span>
                      </div>
                    )}
                    {(traderName === "BetProduct" ||
                      traderName === "Matrix") && (
                      <button
                        className="return-to-site-button"
                        onClick={() => {
                          if (traderName === "Matrix") {
                            window.location.href =
                              "https://api.dev.transferty.com/v1/result/19b0b0476d0f5062738315139b5cd480";
                          } else {
                            window.location.href = "https://1cryptobet.com";
                          }
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          style={{ marginRight: "8px" }}
                        >
                          <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                        </svg>
                        Sayta Geri Dön
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {paymentResult === "success" && (
                    <>
                      <div className="success-animation-wrapper">
                        <Lottie
                          animationData={correctAnimation}
                          className="correct-animation"
                          loop={false}
                        />
                      </div>
                      <div className="success-message">
                        <h3>Köçürmə müvəffəqiyyətlə icra edildi!</h3>
                        <p>
                          Aşağıdakı düyməyə basıb sayta geri dönüş edə
                          bilərsiniz. Bizi seçdiyiniz üçün təşəkkür edirik!
                        </p>
                        {redirectCountdown && redirectCountdown > 0 && (
                          <div className="auto-redirect-notification">
                            <div className="countdown-icon">
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                              >
                                <path d="M15,1H9V3H15M11,14H13V8H11M19.03,7.39L20.45,5.97C20,5.46 19.55,5 19.04,4.56L17.62,6C16.07,4.74 14.12,4 12,4A9,9 0 0,0 3,13A9,9 0 0,0 12,22C17,22 21,17.97 21,13C21,10.88 20.26,8.93 19.03,7.39M12,20A7,7 0 0,1 5,13A7,7 0 0,1 12,6A7,7 0 0,1 19,13A7,7 0 0,1 12,20Z" />
                              </svg>
                            </div>
                            <span>
                              {redirectCountdown} saniyə sonra avtomatik
                              yönləndiriləcəksiniz...
                            </span>
                          </div>
                        )}
                        {(() => {
                          console.log("🔍 TRADER DEBUG - Success State:", {
                            traderName,
                            traderNameType: typeof traderName,
                            isBetProduct: traderName === "BetProduct",
                            isMatrix: traderName === "Matrix",
                            shouldShowButton:
                              traderName === "BetProduct" ||
                              traderName === "Matrix",
                          });
                          return (
                            traderName === "BetProduct" ||
                            traderName === "Matrix"
                          );
                        })() && (
                          <button
                            className="return-to-site-button"
                            onClick={() => {
                              if (traderName === "Matrix") {
                                window.location.href =
                                  "https://api.dev.transferty.com/v1/result/19b0b0476d0f5062738315139b5cd480";
                              } else {
                                window.location.href = "https://1cryptobet.com";
                              }
                            }}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              style={{ marginRight: "8px" }}
                            >
                              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                            </svg>
                            Sayta Geri Dön
                          </button>
                        )}
                      </div>
                    </>
                  )}
                  {paymentResult === "declined" && (
                    <>
                      <div className="failed-animation-wrapper">
                        <Lottie
                          animationData={failedAnimation}
                          className="failed-animation"
                          loop={false}
                        />
                      </div>
                      <div className="failed-message">
                        <h3>
                          {isPaymentAborted === false
                            ? "Ödəmə Rədd Edildi"
                            : "Ödəniş ləğv edildi"}
                        </h3>
                        {declineExplanation && (
                          <div className="decline-explanation">
                            <strong>Səbəb:</strong> {declineExplanation}
                          </div>
                        )}
                        <p>
                          {isPaymentAborted === false
                            ? "Ödənişiniz rədd olundu. Zəhmət olmasa kart məlumatlarınızı yenidən yoxlayın və təkrar cəhd edin."
                            : "Ödənişiniz rədd olundu. Təhlükəsiz şəkildə yönləndirilirsiniz, zəhmət olmasa gözləyin."}
                        </p>
                        {redirectCountdown && redirectCountdown > 0 && (
                          <div className="auto-redirect-notification failed">
                            <div className="countdown-icon">
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                              >
                                <path d="M15,1H9V3H15M11,14H13V8H11M19.03,7.39L20.45,5.97C20,5.46 19.55,5 19.04,4.56L17.62,6C16.07,4.74 14.12,4 12,4A9,9 0 0,0 3,13A9,9 0 0,0 12,22C17,22 21,17.97 21,13C21,10.88 20.26,8.93 19.03,7.39M12,20A7,7 0 0,1 5,13A7,7 0 0,1 12,6A7,7 0 0,1 19,13A7,7 0 0,1 12,20Z" />
                              </svg>
                            </div>
                            <span>
                              {redirectCountdown} saniyə sonra avtomatik
                              yönləndiriləcəksiniz...
                            </span>
                          </div>
                        )}
                        {(traderName === "BetProduct" ||
                          traderName === "Matrix") && (
                          <button
                            className="return-to-site-button failed"
                            onClick={() => {
                              if (traderName === "Matrix") {
                                window.location.href =
                                  "https://api.dev.transferty.com/v1/result/19b0b0476d0f5062738315139b5cd480";
                              } else {
                                window.location.href = "https://1cryptobet.com";
                              }
                            }}
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              style={{ marginRight: "8px" }}
                            >
                              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                            </svg>
                            Sayta Geri Dön
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Notification Container */}
      <NotificationContainer
        notifications={notifications}
        onRemove={removeNotification}
      />
    </div>
  );
};

export default NewPaymentGatewayCard;
